import { QueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import createFetchClient from 'openapi-fetch';
import createClient from 'openapi-react-query';
import type { paths } from '../__generated__/openapi';
import { FlikHttpAuthOptions } from '../auth';
import { DEFAULT_STALE_TIME } from '../caching';
import { createKyClient } from '../ky/client';
import {
  calculateExponentialBackoffDelay,
  DEFAULT_MAX_RETRIES,
  ExponentialBackoffOptions,
} from '../retries';
import { DEFAULT_STALE_PERIOD } from './consts';

interface ReactQueryClientOptions {
  uri: () => string;
  auth: FlikHttpAuthOptions;
  backoff?: ExponentialBackoffOptions;
}

export const createReactQueryClient = ({
  uri,
  auth,
  backoff,
}: ReactQueryClientOptions) => {
  // Create Ky client with auth hooks for consistent auth handling
  const kyClient = createKyClient({
    uri,
    auth,
    backoff,
  });

  // Create fetch client using our Ky client for consistent auth, retry, and error handling
  const fetchClient = createFetchClient<paths>({
    baseUrl: uri(),
    fetch: kyClient, // Use Ky client as the fetch implementation
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchInterval: DEFAULT_STALE_PERIOD,
        retry: (failureCount, error: any) => {
          // if errors are caused by the http client, we need to retry
          if (!(error instanceof HTTPError)) {
            return failureCount < DEFAULT_MAX_RETRIES;
          }

          // For any other scenarios, we don't retry
          return false;
        },
        retryDelay: (attempt: number) => {
          return calculateExponentialBackoffDelay(attempt);
        },
        staleTime: DEFAULT_STALE_TIME,
      },
      mutations: {
        retry: false,
      },
    },
  });

  // Create the API client with the configured fetch client
  // Note: openapi-react-query v0.3.1 doesn't support passing the queryClient directly
  const openapiClient = createClient(fetchClient);

  // Return both the API client and the query client for use in the provider
  return { queryClient, openapiClient };
};
